import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useState } from 'react'
import { getAccountAccessToken } from '../lib/accessToken'
import { copy } from '../lib/i18n'
import {
  formatDateTime,
  formatRelative,
  formatSessionDevice,
  isMobileSession,
  listSessions,
  revokeAllSessions,
  revokeSession,
  type Auth0Session,
} from '../lib/sessions'
import { Button } from '../ui/Button'
import { RangoAlert } from '../ui/RangoFeedback'
import { RangoLink } from '../ui/RangoLink'
import { RangoModal } from '../ui/RangoModal'
import './account.css'

/** MFA ligada quando a conta tem fator inscrito e/ou o tenant força MFA. */
type MfaUser = {
  [claim: string]: unknown
  mfa_factors?: unknown[]
  app_metadata?: Record<string, unknown>
  user_metadata?: Record<string, unknown>
}

export function mfaStatus(
  user: MfaUser | undefined,
  profile?: {
    user_metadata?: Record<string, unknown>
    app_metadata?: Record<string, unknown>
  } | null,
): { enabled: boolean; factors: string[] } {
  const factors: string[] = []
  const candidates = user?.mfa_factors
  if (Array.isArray(candidates)) {
    for (const factor of candidates) {
      if (typeof factor === 'string') factors.push(factor)
      else if (factor && typeof factor === 'object') {
        const type = (factor as { type?: string }).type
        if (type) factors.push(type)
      }
    }
  }
  const forced =
    profile?.user_metadata?.force_mfa === true ||
    profile?.app_metadata?.force_mfa === true ||
    user?.user_metadata?.force_mfa === true ||
    user?.app_metadata?.force_mfa === true
  return { enabled: forced || factors.length > 0, factors }
}

type PopupState =
  | { kind: 'none' }
  | { kind: 'alert'; title: string; message: string }
  | { kind: 'confirm-terminate' }
  | { kind: 'confirm-revoke'; session: Auth0Session }

export function SecurityPage() {
  const { user, getAccessTokenSilently, loginWithRedirect, logout } = useAuth0()
  const [sessions, setSessions] = useState<Auth0Session[]>([])
  const [error, setError] = useState<string>()
  const [busy, setBusy] = useState<string | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [popup, setPopup] = useState<PopupState>({ kind: 'none' })
  const [notice, setNotice] = useState<string>()

  const refresh = useCallback(async () => {
    setError(undefined)
    setLoading(true)
    try {
      const token = await getAccountAccessToken(getAccessTokenSilently)
      const res = await listSessions(token)
      setSessions(res.sessions)
      if (res.error) setError(res.error)
    } catch {
      setError(copy.security.loadError)
    } finally {
      setLoading(false)
    }
  }, [getAccessTokenSilently])

  useEffect(() => {
    void refresh()
  }, [refresh])

  async function onRevoke(id: string) {
    setBusy(id)
    setNotice('')
    try {
      const token = await getAccountAccessToken(getAccessTokenSilently)
      const res = await revokeSession(token, id)
      if (!res.ok) {
        setPopup({
          kind: 'alert',
          title: copy.security.revokeFailed,
          message: copy.security.loadErrorHint,
        })
      } else {
        setOpenId(null)
        await refresh()
      }
    } finally {
      setBusy(null)
    }
  }

  async function runTerminateAll() {
    setBusy('all')
    try {
      const token = await getAccountAccessToken(getAccessTokenSilently)
      const res = await revokeAllSessions(token)
      if (!res.ok) {
        setPopup({
          kind: 'alert',
          title: copy.security.terminateFailed,
          message: copy.security.loadErrorHint,
        })
      } else {
        // A confirmação promete "inclusive este": a sessão do Auth0 já foi
        // encerrada no BFF, então este navegador sai também e volta ao login.
        setSessions([])
        await logout({ logoutParams: { returnTo: window.location.origin } })
      }
    } finally {
      setBusy(null)
    }
  }

  function changePassword() {
    const email = user?.email
    const domain = import.meta.env.VITE_AUTH0_DOMAIN
    const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID
    const connection =
      import.meta.env.VITE_AUTH0_DB_CONNECTION ||
      'Username-Password-Authentication'

    if (email && domain) {
      void fetch(`https://${domain}/dbconnections/change_password`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ client_id: clientId, email, connection }),
      })
        .then((r) => {
          if (r.ok) {
            setNotice(copy.security.passwordSent)
            return
          }
          void loginWithRedirect({
            authorizationParams: { screen_hint: 'login' },
            appState: { returnTo: '/account/security' },
          })
        })
        .catch(() => setNotice(copy.security.passwordFailed))
      return
    }

    const myAccount = import.meta.env.VITE_AUTH0_MY_ACCOUNT_URL
    if (myAccount) window.location.assign(myAccount)
  }

  const mfa = mfaStatus(user, null)

  return (
    <>
      <h1 className="acct-title">{copy.security.title}</h1>
      <p className="acct-sub">{copy.security.subtitle}</p>

      {notice ? (
        <div className="acct-alert-slot">
          <RangoAlert
            tone="positive"
            title={copy.security.passwordSentTitle}
            onDismiss={() => setNotice('')}
          >
            {notice}
          </RangoAlert>
        </div>
      ) : null}

      <div className="acct-grid acct-grid-security">
        <section className="acct-card" aria-labelledby="acct-password-title">
          <div className="acct-card-body">
            <h2 className="acct-card-title" id="acct-password-title">
              {copy.security.passwordTitle}
            </h2>
            <p className="acct-card-desc">{copy.security.passwordDesc}</p>
          </div>
          <div className="acct-card-foot">
            <Button variant="link" onClick={changePassword}>
              {copy.security.passwordAction}
            </Button>
          </div>
        </section>

        <section className="acct-card" aria-labelledby="acct-mfa-title">
          <div className="acct-card-body">
            <h2 className="acct-card-title" id="acct-mfa-title">
              {copy.security.mfaTitle}
            </h2>
            <p className="acct-card-desc">
              {mfa.enabled ? copy.security.mfaOnDesc : copy.security.mfaOffDesc}
            </p>
            <span
              className="acct-status"
              data-on={mfa.enabled ? 'true' : 'false'}
            >
              {mfa.enabled ? copy.security.mfaOn : copy.security.mfaOff}
            </span>
          </div>
        </section>
      </div>

      <div className="acct-sec-head">
        <div>
          <h2 className="acct-card-title">{copy.security.sessionsTitle}</h2>
          <p className="acct-card-desc">{copy.security.sessionsDesc}</p>
        </div>
        <Button
          variant="link"
          tone="danger"
          disabled={busy === 'all' || sessions.length === 0}
          loading={busy === 'all'}
          onClick={() => setPopup({ kind: 'confirm-terminate' })}
        >
          {copy.security.terminateAll}
        </Button>
      </div>

      {error ? (
        <div className="acct-alert-slot">
          <RangoAlert
            tone="warning"
            title={copy.security.loadError}
            action={
              <RangoLink
                onClick={() => void refresh()}
                className="acct-alert-link"
              >
                {copy.common.retry}
              </RangoLink>
            }
          >
            {copy.security.loadErrorHint}
          </RangoAlert>
        </div>
      ) : null}

      <section className="acct-card" aria-label={copy.security.sessionsTitle}>
        {loading ? (
          <p className="acct-card-desc acct-card-inset">{copy.common.loading}</p>
        ) : null}
        {!loading && sessions.length === 0 && !error ? (
          <p className="acct-card-desc acct-card-inset">{copy.security.empty}</p>
        ) : null}
        {sessions.map((session) => {
          const mobile = isMobileSession(session)
          const open = openId === session.id
          return (
            <div key={session.id} className="acct-session-item">
              <div className="acct-session">
                <div
                  className={`acct-session-icon ${mobile ? 'mobile' : 'desktop'}`}
                  aria-hidden="true"
                />
                <div className="acct-session-meta">
                  <strong>{formatSessionDevice(session)}</strong>
                  <span>
                    {copy.security.lastAccess.replace(
                      '{when}',
                      formatRelative(session.updated_at || session.authenticated_at),
                    )}
                  </span>
                </div>
                <Button
                  variant="link"
                  aria-expanded={open}
                  onClick={() => setOpenId(open ? null : session.id)}
                >
                  {open ? copy.security.showLess : copy.security.showMore}
                </Button>
              </div>
              {open ? (
                <div className="acct-session-detail">
                  <dl>
                    <dt>{copy.security.id}</dt>
                    <dd>{session.id}</dd>
                    {session.device?.last_ip ? (
                      <>
                        <dt>{copy.security.ip}</dt>
                        <dd>{session.device.last_ip}</dd>
                      </>
                    ) : null}
                    {session.created_at ? (
                      <>
                        <dt>{copy.security.createdAt}</dt>
                        <dd>{formatDateTime(session.created_at)}</dd>
                      </>
                    ) : null}
                  </dl>
                  <Button
                    variant="link"
                    tone="danger"
                    disabled={busy === session.id}
                    loading={busy === session.id}
                    onClick={() => setPopup({ kind: 'confirm-revoke', session })}
                  >
                    {busy === session.id
                      ? copy.security.revoking
                      : copy.security.revoke}
                  </Button>
                </div>
              ) : null}
            </div>
          )
        })}
      </section>

      <RangoModal
        open={popup.kind === 'alert'}
        title={popup.kind === 'alert' ? popup.title : ''}
        onClose={() => setPopup({ kind: 'none' })}
        footer={
          <Button onClick={() => setPopup({ kind: 'none' })}>
            {copy.common.close}
          </Button>
        }
      >
        {popup.kind === 'alert' ? popup.message : null}
      </RangoModal>

      <RangoModal
        open={popup.kind === 'confirm-terminate'}
        title={copy.security.confirmTerminateTitle}
        tone="danger"
        onClose={() => setPopup({ kind: 'none' })}
        footer={
          <>
            <Button
              variant="secondary"
              data-autofocus
              onClick={() => setPopup({ kind: 'none' })}
            >
              {copy.common.cancel}
            </Button>
            <Button
              onClick={() => {
                setPopup({ kind: 'none' })
                void runTerminateAll()
              }}
            >
              {copy.security.confirmTerminateAction}
            </Button>
          </>
        }
      >
        {copy.security.confirmTerminateMessage}
      </RangoModal>

      <RangoModal
        open={popup.kind === 'confirm-revoke'}
        title={copy.security.revokeTitle}
        tone="danger"
        onClose={() => setPopup({ kind: 'none' })}
        footer={
          <>
            <Button
              variant="secondary"
              data-autofocus
              onClick={() => setPopup({ kind: 'none' })}
            >
              {copy.common.cancel}
            </Button>
            <Button
              onClick={() => {
                if (popup.kind === 'confirm-revoke') {
                  const { id } = popup.session
                  setPopup({ kind: 'none' })
                  void onRevoke(id)
                }
              }}
            >
              {copy.security.revokeAction}
            </Button>
          </>
        }
      >
        {popup.kind === 'confirm-revoke' ? (
          <>
            <p>
              {formatSessionDevice(popup.session)} ·{' '}
              {formatRelative(
                popup.session.updated_at || popup.session.authenticated_at,
              )}
            </p>
            <p>{copy.security.revokeMessage}</p>
          </>
        ) : null}
      </RangoModal>
    </>
  )
}
